import type { FleetAvailabilityDriver, FleetAvailabilityGroup, FleetAvailabilityTruck, TmsFleetAvailability, TmsWriteRow } from '@numeo/types-main-web-app'
import { readCard, runWriteCard, type WriteApproval, type WriteOutcome } from './writeCardApi'

export type { WriteApproval, WriteQuestion } from './writeCardApi'

/** The write card the Agent page runs directly (no chat turn): assign or unassign a load's truck. */
const ASSIGN_CARD = 'tms_loads.assign'

/** The read card behind the picker and the reverse picker: a load's trucks, or a truck's loads. */
const FLEET_CARD = 'tms_fleet.availability'

/** One load of an assign or unassign request: the truck (or a driver, whose truck the card finds), and the confirm options the user chose. */
export interface AssignRequestRow {
    load_id: string
    truck_id?: string
    driver_id?: string
    confirm?: string[]
}

/** The card's parameters, as the direct route takes them. The same params are sent again to answer a question. */
export interface AssignParams {
    method: 'assign' | 'unassign'
    assignments: AssignRequestRow[]
    /** Unassign only: what comes off each load. The card takes no truck id on an unassign: it removes what the load has. */
    part?: 'truck' | 'trailer' | 'both'
    dispatch?: boolean
    message?: string
}

/** A button the server names for a refused row: the value sent back on the row, and the words to show. */
export interface ConfirmOption {
    confirm: string
    label: string
}

/** A row of the write answer: a refused row carries its plain title and the confirm options the server offers for it. */
export type AssignWriteRow = TmsWriteRow & { confirm_choices?: ConfirmOption[]; refusal_title?: string }

/** One load a truck can take, in the truck-mode answer's order (nearest deadhead first, unknown last). */
export interface TruckLoad {
    load_id: string
    load_number: string
    pickup_at: string | null
    pickup?: { lat: number; lng: number }
    deadhead_miles?: number
    ready_in_time?: boolean
    pickup_passed?: boolean
}

/** The truck-mode answer of `tms_fleet.availability`. Local until `@numeo/types-main-web-app` exports it. */
interface TmsFleetTruckLoads {
    as_of: string
    truck_id: string
    truck_number: string
    ready_at: string | null
    location: FleetAvailabilityTruck['location']
    loads: TruckLoad[]
    truncated: boolean
}

/** A driver as the answer may carry it: `inactive` when the driver is inactive (the truck is then home time, ranked last). */
export type FleetDriver = FleetAvailabilityDriver & { inactive?: boolean }

/** A truck of the answer, with its drivers' inactive flags. */
export type FleetTruck = Omit<FleetAvailabilityTruck, 'drivers'> & { drivers: FleetDriver[] }

/** The five groups of trucks. */
export type FleetGroups = Record<FleetAvailabilityGroup, FleetTruck[]>

/** The load the picker is for: its number, pickup place and time, and whether the pickup has already passed. */
export interface FleetLoad {
    load_id: string
    load_number: string
    pickup_at: string | null
    pickup_place?: string
    pickup_passed?: boolean
    pickup?: { lat: number; lng: number }
    deadhead_cutoff_miles: number
    /** The company's IANA zone: the load's dates read in it. */
    timezone?: string
    delivery_passed?: boolean
    delivery_at?: string | null
}

/** The availability answer in load mode, as the picker reads it. */
type FleetAnswer = Omit<TmsFleetAvailability, 'groups' | 'candidates' | 'load'> & {
    groups: FleetGroups
    candidates: FleetTruck[]
    load?: FleetLoad
}

/** Runs the assign or unassign card directly as the user, through the shared write client. Nothing goes to the chat model. */
export function runAssignCard(tabId: string, params: AssignParams, approval?: WriteApproval): Promise<WriteOutcome> {
    return runWriteCard(tabId, ASSIGN_CARD, params, approval)
}

/** The answer for a load's picker: its trucks (`candidates`, in the server's order), the load and the groups. `search` filters on the server. */
export const fetchFreeTrucks = (loadId: string, search?: string): Promise<FleetAnswer> =>
    readCard<FleetAnswer>(FLEET_CARD, { load_id: loadId, ...(search ? { search } : {}) })

/** The loads a truck can take, in the server's order (nearest deadhead first). */
export const fetchTruckLoads = (truckId: string): Promise<TruckLoadsAnswer> =>
    readCard<TmsFleetTruckLoads>(FLEET_CARD, { truck_id: truckId }).then((answer) => ({ loads: answer.loads, truncated: answer.truncated }))

/** What the reverse picker shows of a truck's loads. */
export interface TruckLoadsAnswer {
    loads: TruckLoad[]
    truncated: boolean
}
